/**
 * /university/marketplace/$skillId — single skill detail + install button.
 *
 * The detail page mirrors the standard skill view but is shaped around
 * the marketplace consumer journey: read the description, see the
 * effectiveness + install count, look at example inputs/outputs, install
 * into the current workspace. Founders additionally see the publish
 * button (which opens the redaction preview dialog).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, Download, Globe, Loader2, Sparkles, Star } from 'lucide-react';
import * as React from 'react';

import { Button } from '../../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { useToaster } from '../../../../components/ui/toast';
import { EffectivenessChart } from '../../../../components/university/effectiveness-chart';
import {
  effectivenessTone,
  fmtCount,
  fmtRelative,
  fmtScore,
  MARKETPLACE_STATUS_LABEL,
  TONE_DOT_CLASS,
  TONE_TEXT_CLASS,
} from '../../../../components/university/formatters';
import { RedactionPreviewDialog } from '../../../../components/university/redaction-preview-dialog';
import { learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../stores/auth-store';

function MarketplaceSkillDetailRoute() {
  const { skillId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToaster();
  const [publishOpen, setPublishOpen] = React.useState(false);

  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const isFounder = role === 'founder' || role === 'admin';

  // Re-use the global skill catalog detail endpoint — same skill row,
  // just fetched in marketplace context.
  const skillQ = useQuery({
    queryKey: learningKeys.skill(skillId),
    queryFn: () => learningApi.getSkill(skillId),
  });
  const versionsQ = useQuery({
    queryKey: learningKeys.skillVersions(skillId),
    queryFn: () => learningApi.listSkillVersions(skillId),
    enabled: Boolean(skillId),
  });
  const effectivenessQ = useQuery({
    queryKey: learningKeys.effectiveness(skillId, skillQ.data?.version ?? ''),
    queryFn: () => learningApi.getEffectiveness(skillId, skillQ.data?.version ?? ''),
    enabled: Boolean(skillQ.data?.version),
    retry: false,
  });

  // Install mutation.
  const installM = useMutation({
    mutationFn: () => learningApi.installMarketplaceSkill(skillId),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: learningKeys.skills });
      qc.invalidateQueries({ queryKey: learningKeys.marketplace });
      toast({
        title: 'Skill installed',
        description: `New copy at v${result.installedVersion}.`,
      });
      void navigate({
        to: '/university/skills/$skillId',
        params: { skillId: result.installedSkillId },
      });
    },
    onError: (err: unknown) => {
      toast({
        title: 'Install failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  if (skillQ.isPending) {
    return (
      <section className="mx-auto flex w-full max-w-4xl items-center justify-center gap-2 p-12 text-[12px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading skill…
      </section>
    );
  }
  if (skillQ.isError || !skillQ.data) {
    return (
      <section className="mx-auto flex w-full max-w-4xl flex-col gap-3 p-6">
        <Link
          to="/university/marketplace"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back to marketplace
        </Link>
        <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          Skill not found.
        </div>
      </section>
    );
  }

  const skill = skillQ.data;
  const tone = effectivenessTone(skill.effectivenessScore);
  const statusLabel = MARKETPLACE_STATUS_LABEL[skill.marketplaceStatus] ?? skill.marketplaceStatus;
  const canInstall =
    skill.marketplaceStatus === 'exportable' || skill.marketplaceStatus === 'published';
  const canPublish = isFounder;

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-6">
      <Link
        to="/university/marketplace"
        className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-3 w-3" aria-hidden /> Back to marketplace
      </Link>

      <header className="flex flex-col gap-2 rounded border border-border bg-background p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className={cn('h-2 w-2 shrink-0 rounded-full', TONE_DOT_CLASS[tone])}
              />
              <h1 className="text-[18px] font-semibold tracking-tight text-foreground">
                {skill.name}
              </h1>
              <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                {statusLabel}
              </span>
            </div>
            <span className="mt-0.5 font-mono text-[11px] text-muted-foreground">
              {skill.key} · v{skill.version}
            </span>
            {skill.description ? (
              <p className="mt-2 text-[13px] text-muted-foreground">{skill.description}</p>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button
              type="button"
              size="sm"
              disabled={!canInstall || installM.isPending}
              onClick={() => installM.mutate()}
            >
              {installM.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Installing…
                </>
              ) : (
                <>
                  <Download className="h-3.5 w-3.5" aria-hidden /> Install into workspace
                </>
              )}
            </Button>
            {canPublish ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setPublishOpen(true)}
              >
                <Globe className="h-3.5 w-3.5" aria-hidden /> Publish…
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" aria-hidden />
            score{' '}
            <span className={cn('font-mono font-medium tabular-nums', TONE_TEXT_CLASS[tone])}>
              {fmtScore(skill.effectivenessScore)}
            </span>
          </span>
          <span>
            attempts{' '}
            <span className="font-mono font-medium tabular-nums text-foreground">
              {fmtCount(skill.totalAttempts)}
            </span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Star className="h-3 w-3" aria-hidden />
            license <span className="text-foreground">{skill.licenseType}</span>
          </span>
          <span>updated {fmtRelative(skill.updatedAt)}</span>
        </div>
      </header>

      {effectivenessQ.data ? (
        <EffectivenessChart detail={effectivenessQ.data} />
      ) : effectivenessQ.isPending ? (
        <div className="rounded border border-border bg-background p-3 text-[12px] text-muted-foreground">
          Loading effectiveness…
        </div>
      ) : (
        <div className="rounded border border-border bg-background p-3 text-[12px] text-muted-foreground">
          No effectiveness data yet.
        </div>
      )}

      <Tabs defaultValue="prompt">
        <TabsList>
          <TabsTrigger value="prompt">Prompt</TabsTrigger>
          <TabsTrigger value="examples">Examples</TabsTrigger>
          <TabsTrigger value="versions">Versions</TabsTrigger>
        </TabsList>
        <TabsContent value="prompt" className="mt-3">
          <div className="flex flex-col gap-3">
            <Section title="System prompt">
              <Code body={skill.systemPrompt ?? '(no system prompt)'} />
            </Section>
            <Section title="Prompt template">
              <Code body={skill.promptTemplate} />
            </Section>
            {skill.antiPatterns.length > 0 ? (
              <Section title="Anti-patterns">
                <ul className="list-inside list-disc text-[12px] text-muted-foreground">
                  {skill.antiPatterns.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </Section>
            ) : null}
          </div>
        </TabsContent>
        <TabsContent value="examples" className="mt-3">
          <div className="flex flex-col gap-2">
            {skill.exampleInputs.length === 0 ? (
              <div className="rounded border border-border bg-muted/20 p-3 text-[12px] text-muted-foreground">
                No examples on file.
              </div>
            ) : (
              skill.exampleInputs.map((inp, i) => {
                const out = skill.exampleOutputs[i] ?? '(no matching output)';
                const stableKey = `${i}-${String(inp).slice(0, 40)}`;
                return (
                  <div
                    key={stableKey}
                    className="grid grid-cols-1 gap-px overflow-hidden rounded border border-border bg-border sm:grid-cols-2"
                  >
                    <div className="bg-background p-2">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Input #{i + 1}
                      </div>
                      <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-foreground/90">
                        {String(inp)}
                      </pre>
                    </div>
                    <div className="bg-background p-2">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Output
                      </div>
                      <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-foreground/90">
                        {String(out)}
                      </pre>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TabsContent>
        <TabsContent value="versions" className="mt-3">
          <div className="flex flex-col gap-1.5">
            {versionsQ.isPending ? (
              <div className="px-2 py-4 text-[12px] text-muted-foreground">Loading versions…</div>
            ) : versionsQ.data && versionsQ.data.length > 0 ? (
              versionsQ.data.map((v) => (
                <div
                  key={v.version}
                  className="flex items-center justify-between rounded border border-border bg-background px-3 py-2 text-[12px]"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold">v{v.version}</span>
                    {v.isActive ? (
                      <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-success">
                        active
                      </span>
                    ) : null}
                    {v.authoredByName ? (
                      <span className="text-muted-foreground">by {v.authoredByName}</span>
                    ) : null}
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {fmtRelative(v.createdAt)}
                  </span>
                </div>
              ))
            ) : (
              <div className="rounded border border-border bg-muted/20 p-3 text-[12px] text-muted-foreground">
                Only one version on file.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {canPublish ? (
        <RedactionPreviewDialog
          skill={{
            skillId: skill.skillId,
            key: skill.key,
            name: skill.name,
            version: skill.version,
          }}
          open={publishOpen}
          onOpenChange={setPublishOpen}
        />
      ) : null}
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5 rounded border border-border bg-background p-3">
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Code({ body }: { body: string }) {
  return (
    <pre className="max-h-72 overflow-auto rounded border border-border bg-muted/20 p-2 font-mono text-[11px] leading-relaxed text-foreground/90">
      {body || '(empty)'}
    </pre>
  );
}

export const Route = createFileRoute('/_authenticated/university/marketplace/$skillId')({
  component: MarketplaceSkillDetailRoute,
});
